import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ExternalLink, ArrowRight, Loader2, ShieldCheck, MapPin } from "lucide-react";
import axios from "axios";
import { Logo, DoxMascot } from "@/components/Logo";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public SEO landing page — /for/:state/:trade
 * Data-driven from the compliance database. Every US state × 19 trades = ~950
 * landing pages, zero code changes needed to add more.
 */
export default function PracticePage() {
  const { state, trade } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/public/practice/${state}/${trade}`);
        setData(data);
        // Set document title / meta for SEO
        if (data?.seo?.title) document.title = data.seo.title;
        setMeta("description", data.seo.description);
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [state, trade]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 dash-stage">
        <div className="glass-card p-10 text-center max-w-md">
          <DoxMascot size={56} />
          <h1 className="font-display font-black text-2xl mt-3">Dox doesn&apos;t recognize that state or trade yet.</h1>
          <Link to="/" className="mt-6 inline-block rounded-full bg-dox-orange px-5 py-2.5 text-white font-bold">Back home</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-ink text-white flex items-center justify-center"><Loader2 size={32} className="animate-spin text-dox-orange" /></div>;
  }

  const req = data.requirements || {};

  return (
    <div className="min-h-screen bg-ink text-white dash-stage">
      <div className="noise-overlay" />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-16 sm:pt-44 sm:pb-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-dox-orange mb-4 flex items-center gap-2">
            <MapPin size={12} /> {data.state.name} · {data.trade.name}
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.98]">
            EZDoxAI for <span className="text-gradient-brand">{data.state.name} {data.trade.name} Contractors</span>
          </h1>
          <p className="mt-6 text-white/65 text-lg max-w-2xl leading-relaxed">
            Hire Dox — your AI Compliance Employee — to keep your {data.trade.name.toLowerCase()} business compliant in {data.state.name}. Dox tracks every license, deadline, and filing so you can build.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="practice-cta-signup" onClick={() => navigate("/register")} className="rounded-full bg-dox-orange px-6 py-3.5 text-white font-bold flex items-center gap-2 hover:bg-dox-orangeHover transition-colors">
              Start free trial <ArrowRight size={14} />
            </button>
            {data.official_link && (
              <a href={data.official_link} target="_blank" rel="noreferrer" className="rounded-full glass-strong border border-white/10 px-6 py-3.5 text-white font-semibold flex items-center gap-2 hover:bg-white/10 transition-colors">
                Official {data.issuing_agency.name} <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Requirements grid */}
      <section className="pb-16 sm:pb-24 mx-auto max-w-5xl px-5 sm:px-8">
        <h2 className="font-display font-bold text-2xl mb-6">Licensing requirements</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Fact label="License required" value={req.license_required ? "Yes" : "No"} highlight={req.license_required} />
          <Fact label="Exam required" value={req.exam_required ? "Yes" : "—"} />
          <Fact label="Experience" value={req.typical_experience_years ? `${req.typical_experience_years} years` : "—"} />
          <Fact label="Renewal cycle" value={req.renewal_cycle_months ? `${req.renewal_cycle_months} months` : "—"} />
          <Fact label="Renewal fee" value={req.typical_renewal_fee_usd ? `$${req.typical_renewal_fee_usd}` : "—"} />
          <Fact label="Bond" value={req.typical_bond_usd ? `$${req.typical_bond_usd.toLocaleString()}` : "Not required"} />
          <Fact label="GL insurance limit" value={req.typical_gl_limit_usd ? `$${req.typical_gl_limit_usd.toLocaleString()}` : "Required"} />
          <Fact label="CE hours / cycle" value={req.continuing_education_hours_per_cycle ? `${req.continuing_education_hours_per_cycle}h` : "—"} />
          <Fact label="Workers' Comp" value={req.workers_comp_required ? "Required" : "As applicable"} />
        </div>
        {data.notes && (
          <div className="mt-6 glass-card p-5 text-sm text-white/70 leading-relaxed">
            <span className="text-white/50 uppercase tracking-widest text-[10px] mr-2">Note</span>{data.notes}
          </div>
        )}
      </section>

      {/* Related permits */}
      {data.related_permits?.length > 0 && (
        <section className="pb-16 mx-auto max-w-5xl px-5 sm:px-8">
          <h2 className="font-display font-bold text-2xl mb-6">Common permits</h2>
          <div className="flex flex-wrap gap-2">
            {data.related_permits.map((p) => (
              <div key={p.slug} className="glass-card px-4 py-2 text-sm text-white/80">
                {p.name}
                {p.processing_days_range && (
                  <span className="ml-2 text-[11px] text-white/45">{p.processing_days_range[0]}–{p.processing_days_range[1]}d</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="pb-24 mx-auto max-w-5xl px-5 sm:px-8">
        <h2 className="font-display font-bold text-2xl mb-6">FAQ</h2>
        <div className="space-y-3">
          {data.faqs.map((f, i) => (
            <details key={i} className="glass-card p-5 group">
              <summary className="cursor-pointer text-white font-semibold flex items-center justify-between">
                {f.q}
                <span className="text-dox-orange group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-white/65 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative pb-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="glass-card p-10 text-center">
            <div className="animate-float inline-block"><DoxMascot size={64} /></div>
            <h3 className="font-display font-black text-3xl mt-3">Hand it to Dox.</h3>
            <p className="text-white/60 mt-3 max-w-xl mx-auto">
              14-day free trial. $99/business/month. Cancel any time. Dox pays for itself the first time it prevents a missed filing.
            </p>
            <button data-testid="practice-cta-bottom" onClick={() => navigate("/register")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-dox-orange px-6 py-3.5 text-white font-bold hover:bg-dox-orangeHover transition-colors">
              Start free trial <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Fact({ label, value, highlight }) {
  return (
    <div className={`glass-card p-4 ${highlight ? "border-dox-orange/40" : ""}`}>
      <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">{label}</div>
      <div className={`font-display font-bold text-lg ${highlight ? "text-dox-orange" : "text-white"}`}>{value}</div>
    </div>
  );
}

function setMeta(name, content) {
  if (!content) return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", name); document.head.appendChild(tag); }
  tag.setAttribute("content", content);
}
